import React from 'react';
import ScrollReveal from './ScrollReveal';
import HubSpotForm from './HubSpotForm';

function Home() {
    return (
        <main>

            {/* ====================================================
                HERO
                ==================================================== */}
            <section className="site-section-lg">
                <div className="dt-container dt-center">
                    <ScrollReveal animation="fade-in">
                        <span className="badge-live">Private Beta &middot; v1 MVP</span>
                    </ScrollReveal>
                    <ScrollReveal animation="fade-up" delay={100}>
                        <h1 className="hero-headline">
                            One API to ship every secret<br />
                            <span className="text-accent">your app needs to send.</span>
                        </h1>
                    </ScrollReveal>
                    <ScrollReveal animation="fade-up" delay={200}>
                        <p className="hero-sub dt-mx-auto">
                            Credit cards, recovery codes, customer SSNs, API keys &mdash; over any channel
                            you already use. Dark Transport rides underneath iMessage, SMS, Slack, email.
                            The bytes never live in a mailbox.
                        </p>
                    </ScrollReveal>
                    <ScrollReveal animation="fade-up" delay={300}>
                        <p className="hero-descriptor dt-mx-auto" style={{ marginTop: '12px' }}>
                            Think of it as <strong>SendGrid for secrets.</strong>
                        </p>
                    </ScrollReveal>

                    <ScrollReveal animation="fade-up" delay={400}>
                        <div className="hero-pillars">
                            <div className="hero-pillar">
                                <span className="hero-pillar-icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="11" width="18" height="11" rx="2" />
                                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                    </svg>
                                </span>
                                <div>
                                    <strong>Triple-encrypted by default</strong>
                                    <span className="hero-pillar-detail">AES &rarr; Camellia &rarr; ChaCha20 with PBKDF2-derived per-message keys.</span>
                                </div>
                            </div>
                            <div className="hero-pillar">
                                <span className="hero-pillar-icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12 6 12 12 16 14" />
                                    </svg>
                                </span>
                                <div>
                                    <strong>Bounded retention</strong>
                                    <span className="hero-pillar-detail">Max-views and expiry enforced server-side. Bytes unrecoverable after the policy is met.</span>
                                </div>
                            </div>
                            <div className="hero-pillar">
                                <span className="hero-pillar-icon" aria-hidden="true">
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14 2 14 8 20 8" />
                                        <line x1="9" y1="15" x2="15" y2="15" />
                                    </svg>
                                </span>
                                <div>
                                    <strong>Forensic audit trail</strong>
                                    <span className="hero-pillar-detail">Every send, retrieval, and cipher version logged with fingerprint, actor, IP, payload size.</span>
                                </div>
                            </div>
                        </div>
                    </ScrollReveal>

                    <ScrollReveal animation="fade-up" delay={500}>
                        <div className="dt-flex-center" style={{ marginTop: '36px' }}>
                            <a href="#notify" className="btn-primary-cta">Contact us &rarr;</a>
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            <hr className="section-divider" />

            {/* ====================================================
                THE PROBLEM — single-column narrative
                ==================================================== */}
            <section id="problem" className="site-section">
                <div className="dt-container dt-container--narrow">
                    <ScrollReveal animation="fade-up">
                        <h2 className="section-heading">
                            Every secret you send still lives somewhere.
                        </h2>
                    </ScrollReveal>
                    <ScrollReveal animation="fade-up" delay={100}>
                        <p className="section-subheading">
                            A parent texts a credit card number to their kid at college. A bank asks a customer
                            to &quot;verify the last four of your SSN&quot; over the phone. An IT admin DMs a temp password
                            to a new hire on Slack. A vendor emails an API key to a partner.
                        </p>
                    </ScrollReveal>
                    <ScrollReveal animation="fade-up" delay={200}>
                        <p className="section-subheading">
                            Every one of these is a secret riding over a channel built for conversation,
                            not confidentiality. It sits in inboxes, chat search, and backup tape forever.
                            The sender has no audit. The recipient has no destroy button. The channel itself
                            can&apos;t tell the user whether the request was even legitimate.
                        </p>
                    </ScrollReveal>
                    <ScrollReveal animation="fade-up" delay={300}>
                        <p className="section-subheading">
                            It&apos;s not a tooling problem. It&apos;s an architectural assumption nobody challenged:
                            that the channel that delivers the framing should also carry the payload.
                        </p>
                    </ScrollReveal>
                    <ScrollReveal animation="fade-up" delay={400}>
                        <p className="section-subheading" style={{ color: '#F0F4F8', fontWeight: 600 }}>
                            It doesn&apos;t have to.
                        </p>
                    </ScrollReveal>
                </div>
            </section>

            {/* ====================================================
                TRANSITION VISUAL
                ==================================================== */}
            <ScrollReveal animation="fade-in">
                <div className="transition-visual" aria-hidden="true">
                    <div className="transition-dots">
                        <span className="transition-dot" />
                        <span className="transition-dot" />
                        <span className="transition-dot" />
                        <span className="transition-dot" />
                        <span className="transition-dot" />
                    </div>
                    <span className="transition-arrow">&rarr;</span>
                    <div className="transition-dots">
                        <span className="transition-dot active" />
                        <span className="transition-dot active" style={{ animationDelay: '0.3s' }} />
                        <span className="transition-dot active" style={{ animationDelay: '0.6s' }} />
                    </div>
                </div>
            </ScrollReveal>

            {/* ====================================================
                FEATURES — 3 glass cards
                ==================================================== */}
            <section id="features" className="site-section">
                <div className="dt-container">
                    <ScrollReveal animation="fade-up">
                        <div className="dt-container--narrow dt-center dt-mx-auto" style={{ marginBottom: '56px' }}>
                            <h2 className="section-heading">
                                One layer between your channel and your payload.
                            </h2>
                            <p className="section-subheading">
                                Think of it as SendGrid for secrets. It does exactly what it says, does not
                                surprise you, and gets out of the way of whatever channel you already use.
                            </p>
                        </div>
                    </ScrollReveal>

                    <div className="features-grid">
                        <ScrollReveal animation="fade-up" delay={0}>
                            <div className="glass-card">
                                <div className="pillar-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                    </svg>
                                </div>
                                <h3>Channel-agnostic delivery</h3>
                                <p>
                                    iMessage, SMS, WhatsApp, Slack, Teams, email, Zalo, Messenger &mdash; any of them.
                                    The channel carries the framing and a single-use retrieval URL. Dark Transport
                                    carries the bytes. Your users keep talking the way they already talk.
                                </p>
                            </div>
                        </ScrollReveal>

                        <ScrollReveal animation="fade-up" delay={150}>
                            <div className="glass-card">
                                <div className="pillar-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="23 4 23 10 17 10" />
                                        <polyline points="1 20 1 14 7 14" />
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                    </svg>
                                </div>
                                <h3>Polymorphic encryption</h3>
                                <p>
                                    Triple-encrypted by default. Cipher chains rotate on a schedule under a
                                    frozen primitive allow-list. Burner stacks for top-value bytes &mdash; single-use,
                                    retired the moment they&apos;re used. Cracking yesterday&apos;s stack doesn&apos;t
                                    read tomorrow&apos;s traffic.
                                </p>
                            </div>
                        </ScrollReveal>

                        <ScrollReveal animation="fade-up" delay={300}>
                            <div className="glass-card">
                                <div className="pillar-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                        <polyline points="22 4 12 14.01 9 11.01" />
                                    </svg>
                                </div>
                                <h3>Forensic audit trail</h3>
                                <p>
                                    Every send, retrieval, and admin action writes one row &mdash; with the cipher
                                    fingerprint, actor role, request IP, and payload size denormalized in.
                                    &quot;Every retrieval encrypted under cipher version X between dates Y and Z&quot;
                                    returns from a single-table scan.
                                </p>
                            </div>
                        </ScrollReveal>
                    </div>
                </div>
            </section>

            <hr className="section-divider" />

            {/* ====================================================
                HOW IT WORKS — architecture flow
                ==================================================== */}
            <section id="how-it-works" className="site-section">
                <div className="dt-container">
                    <ScrollReveal animation="fade-up">
                        <div className="dt-container--narrow dt-center dt-mx-auto" style={{ marginBottom: '48px' }}>
                            <h2 className="section-heading">
                                The channel never touches the secret.
                            </h2>
                            <p className="section-subheading">
                                Your app POSTs the payload to Dark Transport. We return a single-use retrieval URL.
                                Your channel carries the URL. The recipient retrieves once. The bytes destroy themselves.
                            </p>
                        </div>
                    </ScrollReveal>

                    <ScrollReveal animation="scale-in">
                        <div className="glass-card-lg">
                            <div className="arch-flow">
                                <div className="dt-center">
                                    <p className="arch-label">Your App</p>
                                    <div className="arch-box">SDK / API call</div>
                                </div>
                                <div className="arch-arrow">&rarr;</div>
                                <div className="dt-center">
                                    <p className="arch-label">Dark Transport</p>
                                    <div className="arch-box arch-box--accent">/api/messages</div>
                                </div>
                                <div className="arch-arrow">&rarr;</div>
                                <div className="dt-center">
                                    <p className="arch-label">Any Channel</p>
                                    <div className="arch-box">iMessage &middot; Slack &middot; Email</div>
                                </div>
                                <div className="arch-arrow">&rarr;</div>
                                <div className="dt-center">
                                    <p className="arch-label">Recipient</p>
                                    <div className="arch-box">Member &middot; Guest &middot; Webhook</div>
                                </div>
                            </div>
                            <p className="text-muted-white dt-center" style={{ fontSize: '0.95rem', maxWidth: '680px', margin: '32px auto 0' }}>
                                The channel only sees the retrieval URL. Dark Transport never sees your channel.
                                The retrieval satisfies whatever recipient flow you choose &mdash; passphrase, MFA,
                                or identity tier &mdash; before bytes are released. Retention policy decides how long they last.
                            </p>
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            {/* ====================================================
                INFRA SIGNAL — built-by
                ==================================================== */}
            <ScrollReveal animation="fade-in">
                <div className="infra-signal-card">
                    <p className="infra-signal-text">
                        Built by <a href="https://www.linkedin.com/in/aschwabe/" target="_blank" rel="noopener noreferrer">Andrew Schwabe</a>
                        &mdash; because every business and every consumer hits the same wall: there is no safe
                        way to share a secret with another person.
                    </p>
                    <p className="infra-signal-sub">
                        25+ years building distributed systems. Cryptographic transport is the layer
                        everyone needs and no one ships.
                    </p>
                </div>
            </ScrollReveal>

            <hr className="section-divider" />

            {/* ====================================================
                COMPATIBILITY — code block + works-with
                ==================================================== */}
            <section className="site-section">
                <div className="dt-container">
                    <ScrollReveal animation="fade-up">
                        <div className="dt-container--narrow dt-center dt-mx-auto" style={{ marginBottom: '32px' }}>
                            <h2 className="section-heading">
                                One API. One key. Zero crypto in your code.
                            </h2>
                            <p className="section-subheading">
                                If you can hit an HTTP endpoint, you can ship secrets safely. Replace your
                                N ad-hoc password-link integrations with one well-defined dependency.
                            </p>
                        </div>
                    </ScrollReveal>

                    <div className="dt-container--narrow dt-mx-auto">
                        <ScrollReveal animation="fade-up" delay={100}>
                            <div className="code-block">
                                <span className="code-comment"># 1. Get an API key in the dashboard</span><br />
                                <span className="code-comment">#    https://darktransport.com/dashboard/keys</span><br /><br />
                                <span className="code-comment"># 2. POST a secret. Get back a single-use retrieval URL.</span><br />
                                curl -X POST https://api.darktransport.com/v1/messages \<br />
                                &nbsp;&nbsp;-H <span className="code-highlight">"Authorization: Bearer $DT_KEY"</span> \<br />
                                &nbsp;&nbsp;-d <span className="code-highlight">'{`{"payload":"4111-1111-1111-1111","passphrase":"shared-otb","max_views":1,"expires_days":7}`}'</span><br /><br />
                                <span className="code-comment"># 3. Paste the URL into iMessage / Slack / email. Done.</span><br />
                                <span className="code-comment"># &larr; response:</span><br />
                                &nbsp;&nbsp;{`{ `}<span className="code-comment">"message_id"</span>: <span className="code-highlight">"msg_8f3c…"</span>,<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;<span className="code-comment">"retrieval_url"</span>: <span className="code-highlight">"https://darktransport.com/r/8f3c…"</span>,<br />
                                &nbsp;&nbsp;&nbsp;&nbsp;<span className="code-comment">"expires_at"</span>: <span className="code-highlight">"2026-05-20T00:00:00Z"</span> {`}`}
                            </div>
                            <p className="text-dim dt-center" style={{ fontSize: '0.8rem', marginTop: '12px' }}>
                                OpenAPI spec at <code>/apidocs</code>. SDKs (Python, Node, Go) on the v2 roadmap.
                            </p>
                        </ScrollReveal>

                        <ScrollReveal animation="fade-up" delay={200}>
                            <div className="works-with" style={{ marginTop: '48px' }}>
                                <p className="works-with-label">Channel-agnostic by design &mdash; rides on top of</p>
                                <div className="works-with-row">
                                    <span className="works-with-item">iMessage</span>
                                    <span className="works-with-item">SMS</span>
                                    <span className="works-with-item">WhatsApp</span>
                                    <span className="works-with-item">Slack</span>
                                    <span className="works-with-item">Microsoft Teams</span>
                                    <span className="works-with-item">Email</span>
                                    <span className="works-with-item">Telegram</span>
                                    <span className="works-with-item">Zalo</span>
                                    <span className="works-with-item">FB Messenger</span>
                                    <span className="works-with-item">Custom in-app</span>
                                </div>
                            </div>
                        </ScrollReveal>
                    </div>
                </div>
            </section>

            <hr className="section-divider" />

            {/* ====================================================
                TIERS — DT-specific
                ==================================================== */}
            <section id="tiers" className="site-section">
                <div className="dt-container">
                    <ScrollReveal animation="fade-up">
                        <div className="dt-container--narrow dt-center dt-mx-auto" style={{ marginBottom: '40px' }}>
                            <h2 className="section-heading">One platform. Three jobs.</h2>
                            <p className="section-subheading">
                                From async secret transport for everyday integration work, to synchronous
                                dual-presence transfers for the highest-value bytes, to consumer-owned identity
                                confirmation that ends &quot;verify your SSN over the phone.&quot;
                            </p>
                        </div>
                    </ScrollReveal>

                    <div className="tiers-band">
                        <ScrollReveal animation="fade-up" delay={0}>
                            <div className="tier-row tier-row--primary">
                                <div>
                                    <div className="tier-when">Shipping &mdash; v1 MVP</div>
                                    <h3>SendGrid for secrets.</h3>
                                    <p>An API for developers + a brand-visible PWA for consumers. Async send, retrieve, audit. Channel-agnostic delivery. Triple-encrypted, retention-bounded.</p>
                                </div>
                                <div className="tier-chip">API &middot; PWA &middot; WEBHOOKS</div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal animation="fade-up" delay={100}>
                            <div className="tier-row tier-row--amber">
                                <div>
                                    <div className="tier-when">Next &mdash; post LAB-003</div>
                                    <h3>Mission Impossible mode.</h3>
                                    <p>Synchronous dual-presence transfers. Both sides online, both MFA, both confirm &mdash; payload cryptographically destroyed on retrieval. For defense, finance, regulated transfers.</p>
                                </div>
                                <div className="tier-chip">DUAL-PRESENCE &middot; BURNER &middot; MFA</div>
                            </div>
                        </ScrollReveal>
                        <ScrollReveal animation="fade-up" delay={200}>
                            <div className="tier-row tier-row--danger">
                                <div>
                                    <div className="tier-when">The bet &mdash; post LAB-004</div>
                                    <h3>Identity Vault.</h3>
                                    <p>Consumers store their PII once. Businesses confirm a value they already hold via mutual hash &mdash; neither side speaks it aloud. The end of &quot;verify your SSN over the phone.&quot;</p>
                                </div>
                                <div className="tier-chip">ANTI-SCAM &middot; ZERO-LEAK</div>
                            </div>
                        </ScrollReveal>
                    </div>
                </div>
            </section>

            <hr className="section-divider" />

            {/* ====================================================
                WHAT THIS IS NOT
                ==================================================== */}
            <section className="site-section">
                <div className="dt-container">
                    <ScrollReveal animation="fade-up">
                        <div className="dt-container--narrow dt-center dt-mx-auto" style={{ marginBottom: '36px' }}>
                            <h2 className="section-heading">What Dark Transport is not.</h2>
                            <p className="section-subheading">
                                We are the transport layer for secrets. We don&apos;t try to be more than that.
                            </p>
                        </div>
                    </ScrollReveal>
                    <ScrollReveal animation="fade-up" delay={100}>
                        <div className="not-grid">
                            <div className="not-item">
                                <span className="not-icon">&times;</span>
                                <div>
                                    <strong>Not a password manager.</strong>
                                    <span className="not-detail">That&apos;s 1Password, Bitwarden. They store secrets at rest. We move them between people.</span>
                                </div>
                            </div>
                            <div className="not-item">
                                <span className="not-icon">&times;</span>
                                <div>
                                    <strong>Not a messaging app.</strong>
                                    <span className="not-detail">That&apos;s Signal, iMessage, WhatsApp. We ride underneath every channel &mdash; we don&apos;t replace one.</span>
                                </div>
                            </div>
                            <div className="not-item">
                                <span className="not-icon">&times;</span>
                                <div>
                                    <strong>Not a cloud secrets store.</strong>
                                    <span className="not-detail">That&apos;s Vault, AWS Secrets Manager. They serve infrastructure. We serve humans (and the apps that talk to them).</span>
                                </div>
                            </div>
                            <div className="not-item">
                                <span className="not-icon">&times;</span>
                                <div>
                                    <strong>Not a KMS.</strong>
                                    <span className="not-detail">No key custody for your service. We handle the per-message cipher chain; you handle your code.</span>
                                </div>
                            </div>
                        </div>
                    </ScrollReveal>
                </div>
            </section>

            <hr className="section-divider" />

            {/* ====================================================
                STAY IN THE LOOP — email capture
                ==================================================== */}
            <section id="notify" className="site-section-lg">
                <div className="dt-container">
                    <div className="dt-container--narrow dt-center dt-mx-auto">
                        <ScrollReveal animation="fade-up">
                            <span className="badge-live">Private Beta</span>
                            <h2 className="section-heading" style={{ marginTop: '24px' }}>Get on the beta list.</h2>
                            <p className="section-subheading" style={{ marginBottom: '28px' }}>
                                Private beta opens orgs by invite. Drop your email for an invite, integration
                                help, and updates as we ship Mission Impossible mode and the Identity Vault.
                            </p>
                        </ScrollReveal>
                        <ScrollReveal animation="fade-up" delay={150}>
                            <div className="signup-form-wrap">
                                <HubSpotForm
                                    portalId="244482894"
                                    formId="a3d7a374-b98c-488a-ae28-782bfeeecd31"
                                    pageName="Dark Transport — Beta capture"
                                    submitLabel="Contact us"
                                    successMessage="Thanks — we'll be in touch."
                                    className="signup-form"
                                    layout="inline"
                                    fields={[
                                        { name: 'email', type: 'email', required: true, placeholder: 'you@company.com' },
                                    ]}
                                />
                            </div>
                            <p className="text-dim" style={{ fontSize: '0.8rem', marginTop: '16px' }}>
                                No spam. We only email about the beta and meaningful updates.
                            </p>
                        </ScrollReveal>
                    </div>
                </div>
            </section>

        </main>
    );
}

export default Home;
